import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import type { FanResponseDto } from '@api'
import { useUnit } from 'effector-react'
import { usePersistentState } from 'react-persistent-state'
import { useQuery } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { $chats, $fanId } from '@/models/chattingUI/chats/chats.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'

import { useAudioFile } from '../hooks/useAudioFile'
import type { FanCountdown } from '../types'

export type CountdownsMap = Record<number, FanCountdown>
interface Props {
  openChat: (fanId: FanResponseDto['id']) => Promise<void>
}

type CountdownContextValue = ReturnType<typeof useComputeCountdowns>
export const CountdownsContext = createContext<CountdownContextValue | null>(
  null,
)

export const useCountdowns = () => {
  const context = useContext(CountdownsContext)

  if (!context) {
    throw new Error('useChats must be used within ChatsListContextProvider')
  }

  return context
}

export const CountdownsContextProvider = memo(
  ({
    children,
    values,
  }: {
    children: React.ReactNode
    values: CountdownContextValue
  }) => {
    return (
      <CountdownsContext.Provider value={values}>
        {children}
      </CountdownsContext.Provider>
    )
  },
)
CountdownsContextProvider.displayName = 'CountdownsContextProvider'

export const useComputeCountdowns = ({ openChat }: Props) => {
  const chats = useUnit($chats)
  const fanId = useUnit($fanId)
  const [countdowns, setCountdowns] = usePersistentState<CountdownsMap>(
    {},
    { storageKey: 'countdowns' },
  )
  const deleteCountdown = useCallback(
    (id: number) => {
      setCountdowns((prev) => {
        const temp = { ...prev }

        delete temp[id]

        return temp
      })
    },
    [setCountdowns],
  )
  const checkCountdownExpiry = useCallback(
    (id: number) => {
      const countdown = countdowns[id]

      // Countdown is active
      if (countdown && countdown.end < Date.now()) {
        deleteCountdown(id)
      }
    },
    [countdowns, deleteCountdown],
  )

  const { countdownSoundType = 'alarm_1' } = useSettingsContext()
  const activeAlarmsRef = useRef<number[]>([])

  const toast = useToast()
  const { handlePlay, handlePause, setAudio } = useAudioFile()
  const timers = useRef<Record<string, NodeJS.Timeout>>({})
  const getCountdownAudio = useQuery(
    ['countdownSound', countdownSoundType],
    () =>
      import(`../../../assets/audio/${countdownSoundType}.mp3`).then(
        (module): string => module.default,
      ),
    {
      onSuccess: setAudio,
      enabled: false,
    },
  )

  const handleMoveToChat = useCallback(
    (fanId: FanResponseDto['id']) => {
      openChat(fanId)
      handlePause()
    },
    [handlePause, openChat],
  )

  const alarmCountdown = useCallback(
    async (
      fanId: FanResponseDto['id'],
      chatName: string,
      playAudio: boolean,
    ) => {
      const isAlarmActive = activeAlarmsRef.current.find(
        (alarm) => alarm === fanId,
      )

      if (isAlarmActive) return

      activeAlarmsRef.current = [...activeAlarmsRef.current, fanId]
      if (playAudio) {
        await getCountdownAudio.refetch().then(handlePlay)
      }
      toast.success(`Countdown for chat with ${chatName} ended!`, {
        action: () => handleMoveToChat(fanId),
        actionText: 'Go to chat',
        duration: Infinity,
        autoCloseDisabled: true,
        onClose: () => {
          deleteCountdown(fanId)
        },
      })
    },
    [toast, getCountdownAudio, handlePlay, handleMoveToChat, deleteCountdown],
  )

  useEffect(() => {
    const relevantCountdowns = Object.keys(countdowns)

    activeAlarmsRef.current = activeAlarmsRef.current.filter(
      (activeAlarm) => relevantCountdowns.indexOf(String(activeAlarm)) >= 0,
    )
  }, [countdowns])

  useEffect(() => {
    for (const [fanId, { end, chatName }] of Object.entries(countdowns)) {
      if (!end || end < Date.now() - 10 * 60 * 1000) {
        // Deleting after 10 minutes
        deleteCountdown(Number(fanId))

        continue
      }

      if (end <= Date.now()) {
        alarmCountdown(Number(fanId), chatName, false)

        continue
      }

      if (timers.current[fanId]) {
        clearTimeout(timers.current[fanId])
      }

      timers.current[fanId] = setTimeout(() => {
        alarmCountdown(Number(fanId), chatName, true)
      }, end - Date.now())
    }
  }, [countdowns, deleteCountdown, alarmCountdown, chats])

  const addCountdown = useCallback(
    (id: number, chatName: string, minutes: number) => {
      setCountdowns((prev) => ({
        ...prev,
        [id]: {
          end: Date.now() + minutes * 60 * 1000,
          chatName,
        },
      }))
      toast.success('Countdown started successfully!', { duration: 3000 })
    },
    [toast, setCountdowns],
  )

  useEffect(() => {
    if (fanId) {
      checkCountdownExpiry(fanId)
    }
  }, [fanId, checkCountdownExpiry])

  const value = useMemo(
    () => ({
      countdowns,
      addCountdown,
      deleteCountdown,
      checkCountdownExpiry,
    }),
    [countdowns, addCountdown, deleteCountdown, checkCountdownExpiry],
  )

  return value
}
